import { __ } from "@wordpress/i18n";
import { useBlockProps, InspectorControls } from "@wordpress/block-editor";
import { registerBlockType } from "@wordpress/blocks";
import { PanelBody, ComboboxControl } from "@wordpress/components";
import { useState, useEffect } from "@wordpress/element";
import apiFetch from "@wordpress/api-fetch";
import { addQueryArgs } from "@wordpress/url";

const BLOCK_NAME = "wpbean-pgs/product-gallery";

function decodeHtml(html) {
    const el = document.createElement("textarea");
    el.innerHTML = html;
    return el.value;
}

function productLabel(id, renderedTitle) {
    return `${decodeHtml(renderedTitle)} (#${id})`;
}

// ── Icons ──────────────────────────────────────────────────────────────────────

function ZoomIcon() {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            width="36"
            height="36"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
            <circle cx="12" cy="13" r="4" />
            <line x1="12" y1="11" x2="12" y2="15" />
            <line x1="10" y1="13" x2="14" y2="13" />
        </svg>
    );
}

// ── Placeholder component ──────────────────────────────────────────────────────

function GalleryPlaceholder({ productId, productLabel }) {
    const adminUrl = window?.WPBean_PGS_Options?.adminUrl ?? "";
    const rulesUrl = adminUrl
        ? `${adminUrl}admin.php?page=wpbean-product-gallery-rules`
        : "#";

    const thumbStyle = {
        height: "66px",
        backgroundColor: "#dde0e4",
        borderRadius: "3px",
    };

    const contextNote =
        productId && productLabel
            ? `${productLabel} (#${productId})`
            : __(
                  "current product",
                  "wpbean-product-gallery-slider-for-woocommerce",
              );

    return (
        <div
            style={{
                position: "relative",
                display: "flex",
                flexDirection: "row",
                gap: "8px",
                minHeight: "310px",
                borderRadius: "4px",
                overflow: "hidden",
            }}
        >
            {/* Skeleton: left thumbnail strip */}
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "6px",
                    width: "74px",
                    flexShrink: 0,
                }}
            >
                {[0, 1, 2, 3].map((i) => (
                    <div key={i} style={thumbStyle} />
                ))}
            </div>

            {/* Skeleton: main image area */}
            <div
                style={{
                    flex: 1,
                    backgroundColor: "#dde0e4",
                    borderRadius: "3px",
                }}
            />

            {/* Overlay */}
            <div
                style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "rgba(255,255,255,0.92)",
                    padding: "28px 24px",
                    textAlign: "center",
                    gap: "10px",
                }}
            >
                <span style={{ color: "#3858e9", lineHeight: 1 }}>
                    <ZoomIcon />
                </span>

                <strong
                    style={{
                        display: "block",
                        fontSize: "14px",
                        fontWeight: 600,
                        color: "#1e1e1e",
                        lineHeight: 1.4,
                    }}
                >
                    {__(
                        "WPBean Product Gallery",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                </strong>

                <p
                    style={{
                        margin: 0,
                        fontSize: "12.5px",
                        color: "#757575",
                        maxWidth: "300px",
                        lineHeight: 1.6,
                    }}
                >
                    {__(
                        "The gallery renders here on the frontend based on your active Gallery Rule. This block has no individual settings.",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                </p>

                <p
                    style={{
                        margin: 0,
                        fontSize: "12px",
                        color: "#3858e9",
                        fontWeight: 500,
                    }}
                >
                    {__(
                        "Showing gallery for: ",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                    <em>{contextNote}</em>
                </p>

                <a
                    href={rulesUrl}
                    style={{
                        display: "inline-block",
                        marginTop: "6px",
                        padding: "7px 18px",
                        backgroundColor: "#3858e9",
                        color: "#fff",
                        borderRadius: "4px",
                        fontSize: "12px",
                        fontWeight: 500,
                        textDecoration: "none",
                        lineHeight: 1.5,
                        boxShadow: "0 1px 3px rgba(56,88,233,0.35)",
                    }}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                >
                    {__(
                        "Manage Gallery Rules ↗",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                </a>
            </div>
        </div>
    );
}

// ── Block edit component ───────────────────────────────────────────────────────

function Edit({ attributes, setAttributes }) {
    const { productId } = attributes;

    // The saved selection — kept separately so the search results never evict it.
    const [selectedOption, setSelectedOption] = useState(null);
    const [searchOptions, setSearchOptions] = useState([]);
    const [filterValue, setFilterValue] = useState("");

    // Merge: always keep the selected option visible regardless of search results.
    const options = selectedOption
        ? [
              selectedOption,
              ...searchOptions.filter((o) => o.value !== selectedOption.value),
          ]
        : searchOptions;

    // On mount: if a productId is already saved, fetch its title once.
    useEffect(() => {
        if (!productId) return;
        apiFetch({
            path: addQueryArgs("/wp/v2/product", {
                include: [productId],
                per_page: 1,
                _fields: "id,title",
            }),
        })
            .then((results) => {
                if (results?.[0]) {
                    const p = results[0];
                    setSelectedOption({
                        value: p.id,
                        label: productLabel(p.id, p.title.rendered),
                        title: decodeHtml(p.title.rendered),
                    });
                }
            })
            .catch(() => {});
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch matching products whenever the user types in the combobox.
    useEffect(() => {
        const query = {
            search: filterValue,
            per_page: 20,
            status: "publish",
            _fields: "id,title",
        };
        apiFetch({ path: addQueryArgs("/wp/v2/product", query) })
            .then((results) => {
                setSearchOptions(
                    (results ?? []).map((p) => ({
                        value: p.id,
                        label: productLabel(p.id, p.title.rendered),
                        title: decodeHtml(p.title.rendered),
                    })),
                );
            })
            .catch(() => {});
    }, [filterValue]);

    const blockProps = useBlockProps({
        style: {
            padding: 0,
            border: "1px dashed #c3c4c7",
            borderRadius: "4px",
            overflow: "hidden",
        },
    });

    return (
        <>
            <InspectorControls>
                <PanelBody
                    title={__(
                        "Product",
                        "wpbean-product-gallery-slider-for-woocommerce",
                    )}
                    initialOpen={true}
                >
                    <ComboboxControl
                        label={__(
                            "Override product",
                            "wpbean-product-gallery-slider-for-woocommerce",
                        )}
                        help={__(
                            "Optional. Leave blank to display the gallery for the current product page.",
                            "wpbean-product-gallery-slider-for-woocommerce",
                        )}
                        value={productId || null}
                        options={options}
                        onFilterValueChange={(val) => setFilterValue(val)}
                        onChange={(val) => {
                            const id = val ? parseInt(val, 10) : 0;
                            setAttributes({ productId: id });
                            const match = options.find((o) => o.value === id);
                            setSelectedOption(match ?? null);
                        }}
                        allowReset
                    />
                </PanelBody>
            </InspectorControls>

            <div {...blockProps}>
                <GalleryPlaceholder
                    productId={productId}
                    productLabel={selectedOption?.title ?? ""}
                />
            </div>
        </>
    );
}

// ── Register ───────────────────────────────────────────────────────────────────

registerBlockType(BLOCK_NAME, {
    edit: Edit,
    // Dynamic block: all output is server-rendered, save returns null.
    save: () => null,
});
